/**
 * Fait : le chemin ORDINAIRE par lequel le client parle au serveur, et — ce
 *   qui compte davantage — LA TRADUCTION UNIQUE d'une réponse en objet
 *   lisible : ce que le serveur refuse, ce que la passerelle bredouille, et
 *   la NON-RÉPONSE d'un réseau coupé.
 *
 *   IL N'EST PLUS « LE SEUL ENDROIT », et cette phrase a été retirée le
 *   2026-08-24 (critère 144). Le dépôt d'un document emprunte un second
 *   chemin — `envoi-avec-progression.ts` — parce que `fetch` ne sait pas dire
 *   où en est un envoi, et que dix mégaoctets sur une liaison familiale sont
 *   une attente muette qu'on ne distingue pas d'un blocage. Ce qui reste
 *   unique, c'est `traduitUneReponse` : les deux chemins passent par elle, et
 *   ne peuvent donc pas diverger sur ce qu'est un refus.
 *
 *   POURQUOI CE MODULE EXISTE, écrit le 2026-08-07 : cette fonction était
 *   recopiée à l'identique dans deux fichiers, et aucune des deux copies
 *   n'entourait `fetch`. Une coupure de réseau ne rendait donc pas une erreur :
 *   elle levait, et l'écran restait sur « Chargement… » indéfiniment, sans un
 *   mot. Mesuré sur les quatre écrans du chantier « administration des
 *   comptes ». Pour quelqu'un qui voit mal, un lecteur d'écran annonçait alors
 *   un travail en cours qui n'arriverait jamais.
 *
 *   CE RÉCIT NE COUVRAIT QUE LA MOITIÉ DU SUJET, et l'a laissé croire clos
 *   pendant deux semaines. Entourer `fetch` attrape la coupure FRANCHE. Une
 *   passerelle debout dont l'amont est tombé, elle, RÉPOND — et sa réponse
 *   partait par le chemin du refus ordinaire. Trouvé le 2026-08-21 par une
 *   épreuve indépendante ; la distinction se fait désormais plus bas, sur la
 *   question « qui parle ? ».
 *
 * Ne fait pas : il ne décide d'aucun droit, n'affiche rien, et ne réessaie
 *   pas tout seul. Ce que le serveur refuse, il le rend tel quel — le client
 *   ne cache jamais un refus derrière une jolie phrase inventée.
 */

import type { ErreurDuProduit } from '@aymoai/shared';

/**
 * Ce qu'un appel rend : la valeur attendue, ou l'erreur du produit.
 *
 * `injoignable` distingue « le serveur a dit non » de « le serveur n'a rien
 * dit ». Ce n'est PAS une famille d'erreur de plus : les familles sont le
 * contrat des réponses du serveur, et une absence de réponse n'en est pas
 * une. Les écrans s'en servent pour ne pas confondre une panne avec un refus
 * — confusion qui, sans ce drapeau, renverrait quelqu'un vers l'écran
 * d'entrée en lui laissant croire qu'il a été déconnecté.
 */
export type Issue<T> =
  | { readonly bon: true; readonly valeur: T }
  | { readonly bon: false; readonly erreur: ErreurDuProduit; readonly injoignable?: true };

/** L'erreur rendue quand le serveur n'a pas répondu du tout. */
const INJOIGNABLE: ErreurDuProduit = {
  famille: 'erreur_interne',
  message:
    'L’atelier n’a pas répondu. Vérifiez votre connexion, puis réessayez — rien n’a été perdu.',
};

/**
 * Le serveur n'a rien dit : coupure franche, envoi interrompu, onglet fermé.
 * Le motif exact (DNS, refus de connexion, coupure en cours de route) ne
 * change rien à ce que la personne peut faire : réessayer.
 */
export function sansReponse<T>(): Issue<T> {
  return { bon: false, erreur: INJOIGNABLE, injoignable: true };
}

/**
 * Traduit une réponse REÇUE — le code et le corps déjà lu — en issue.
 *
 * QUI PARLE ? Toute réponse de l'API porte une erreur du produit, avec sa
 * famille : c'est le contrat. Un refus sans famille lisible ne vient donc PAS
 * de l'atelier — c'est la passerelle qui répond à sa place, son amont étant
 * tombé. Ce n'est pas un refus, c'est une absence, et elle se traite comme
 * telle.
 *
 * Corrigé le 2026-08-21, cas trouvé par une épreuve indépendante : quand
 * nginx rendait 502 pendant un déploiement, l'écran renvoyait à l'entrée et
 * redemandait son mot de passe à quelqu'un dont la session était vivante.
 * Le `catch` du `fetch` n'attrape que la coupure FRANCHE ; une réponse bien
 * reçue mais illisible passait par le chemin du refus ordinaire.
 */
export function traduitUneReponse<T>(statut: number, corps: unknown): Issue<T> {
  if (statut === 204) return { bon: true, valeur: undefined as T };
  if (statut >= 200 && statut < 300) return { bon: true, valeur: corps as T };

  const duProduit =
    typeof corps === 'object' &&
    corps !== null &&
    typeof (corps as ErreurDuProduit).famille === 'string';

  if (!duProduit) return sansReponse<T>();

  return { bon: false, erreur: corps as ErreurDuProduit };
}

/** Appelle l'API et traduit la réponse — jamais d'exception, pour rien. */
export async function appelle<T>(
  chemin: string,
  options: { methode?: string; corps?: unknown } = {},
): Promise<Issue<T>> {
  let reponse: Response;

  try {
    reponse = await fetch(chemin, {
      method: options.methode ?? 'GET',
      credentials: 'same-origin',
      ...(options.corps === undefined
        ? {}
        : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(options.corps) }),
    });
  } catch {
    return sansReponse<T>();
  }

  // Le corps est lu ICI, la conclusion est tirée AILLEURS : un 204 n'a rien à
  // analyser, et `traduitUneReponse` le sait — c'est elle qui porte la règle.
  const corps: unknown = await reponse.json().catch(() => null);

  return traduitUneReponse<T>(reponse.status, corps);
}
